import {cn} from '../../lib/utils';
import {Button} from '../Button';
import {Icon} from '../Icon';
import {ModalIcon} from '../Modal';
import {Typography} from '../Typography';

import type {IconName} from '../Icon';
import type {ModalIconVariant} from '../Modal';
import type {FC, MouseEvent} from 'react';

export type ToastCardAction = {
  label: string;
  onClick: () => void;
};

export type ToastCardProps = {
  iconName: IconName;
  iconVariant?: ModalIconVariant;
  title: string;
  description: string;
  action?: ToastCardAction;
  onClose: () => void;
  closeLabel: string;
};

export const ToastCard: FC<ToastCardProps> = ({
  iconName,
  iconVariant,
  title,
  description,
  action,
  onClose,
  closeLabel
}) => {
  const handleClose = (e: MouseEvent) => {
    e.stopPropagation();
    onClose();
  };

  const handleAction = () => {
    action?.onClick();
    onClose();
  };

  return (
    <div
      data-slot="toast-card"
      className={cn(
        'relative flex w-fit max-w-[426px] items-start justify-end gap-4',
        'rounded-xl border border-border bg-background-surface shadow-xs',
        action ? 'py-4 ps-4 pe-12' : 'p-4'
      )}
    >
      {iconVariant ? (
        <ModalIcon icon={iconName} variant={iconVariant} />
      ) : (
        <Icon name={iconName} width={40} height={40} className="shrink-0" />
      )}

      <div className="flex mt-0.5 min-w-0 flex-1 flex-col gap-0.5 pe-9 text-start">
        <Typography
          size="sm"
          weight="semibold"
          color="secondary"
          className="whitespace-normal line-clamp-2"
        >
          {title}
        </Typography>
        <Typography size="sm" color="tertiary" className="whitespace-normal line-clamp-3">
          {description}
        </Typography>
      </div>

      {action && (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={handleAction}
          className="mt-0.5 shrink-0 font-body"
        >
          {action.label}
        </Button>
      )}

      <Button
        type="button"
        variant="tertiary"
        size="icon-sm"
        onClick={handleClose}
        aria-label={closeLabel}
        className="absolute end-[7px] top-[7px] hover:bg-transparent"
      >
        <Icon name="x-close" width={20} height={20} />
      </Button>
    </div>
  );
};
